import _ from "lodash";
import moment from "moment";

/**
 * Shared by the two Yardi-side edit forms (AmendmentEdit, LeaseAgreementEdit).
 */

/**
 * `src/components/Inputs/Input.jsx` runs `moment(value).format("YYYY-MM-DD")` on
 * every change of a date input, so merely FOCUSING an empty one writes the
 * literal string "Invalid date" into form state (see the note on
 * Leases/Form.tsx). Sent as-is that is a 422 at best and a garbage date at
 * worst, so every date is normalised on the way out: strict `YYYY-MM-DD` first,
 * because that is what both a `date` column and a native date input produce,
 * then ISO 8601 so a value that ever arrives with a time on it is kept rather
 * than silently nulled, and null for anything else — which is also how a user
 * CLEARS a date.
 */
export const cleanDate = (value: any): string | null => {
	if (_.isNil(value) || value === "" || value === "Invalid date") return null;
	const exact = moment(value, "YYYY-MM-DD", true);
	if (exact.isValid()) return exact.format("YYYY-MM-DD");
	const iso = moment(value, moment.ISO_8601, true);
	return iso.isValid() ? iso.format("YYYY-MM-DD") : null;
};

/**
 * A locked field with no value renders as an empty grey box, which reads as
 * "not loaded" rather than "Yardi sent nothing".
 */
export const dash = (value: any) => (_.isNil(value) || value === "" ? "—" : value);

/**
 * `Input` gives a number field `parseFloat`, so a cleared box arrives as NaN and
 * a blank one as "". Both mean "no value", and null is what the API wants.
 */
export const cleanNumber = (value: any): number | null => {
	if (_.isNil(value) || value === "" || (_.isNumber(value) && _.isNaN(value))) return null;
	const parsed = parseFloat(`${value}`);
	return _.isNaN(parsed) ? null : parsed;
};

/** An empty text box means "cleared", which is null rather than "". */
export const cleanText = (value: any): string | null => {
	if (_.isNil(value)) return null;
	const trimmed = `${value}`.trim();
	return trimmed === "" ? null : trimmed;
};
